import { useMemo } from "react";
import { useFormState } from "react-final-form";

import {
  getExitStrategyOptions,
  isExitStrategyHidden,
} from "../../../constants/selectOptions/selectOptionsExitStrategy";
import RffSelectField from "./RffSelectField";

/**
 * Reads the live `loanProductType` and current Exit Strategy value off the form
 * and returns `{ options, hidden }` — product-type-scoped options (with any
 * existing out-of-list value appended) plus whether the field should be hidden
 * entirely (DSCR / Other). Use in any form where `loanProductType` and the
 * exit-strategy field both live on the same form values.
 */
export const useExitStrategyOptions = (name = "exitStrategy") => {
  const { values } = useFormState({ subscription: { values: true } });
  const productType = values?.loanProductType;
  const currentValue = values?.[name];

  const options = useMemo(
    () => getExitStrategyOptions(productType, currentValue),
    [productType, currentValue],
  );

  return { options, hidden: isExitStrategyHidden(productType) };
};

/**
 * Exit Strategy select whose options depend on the loan's Product Type. Renders
 * nothing for product types where Exit Strategy doesn't apply (DSCR / Other).
 * Drop-in for a `loanProductType`-aware form (create loan form, etc.).
 */
const RffExitStrategyField = ({
  name = "exitStrategy",
  label = "Exit Strategy",
  ...rest
}) => {
  const { options, hidden } = useExitStrategyOptions(name);

  if (hidden) return null;

  return (
    <RffSelectField name={name} label={label} options={options} {...rest} />
  );
};

export default RffExitStrategyField;
